// src/pages/AssessmentReview.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../contexts/AssessmentContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Comment as CommentIcon,
  AttachFile as AttachFileIcon,
  Visibility as VisibilityIcon,
  Edit as ReplaceIcon,
  Close as CloseIcon
} from '@mui/icons-material';

export default function AssessmentReview() {
  const navigate = useNavigate();
  const { 
    assessmentData, 
    getAnswerForQuestion, 
    getAnsweredQuestionsCount, 
    getTotalQuestionsCount,
    hasValidAssessment,
    getCurrentSessionToken,
    updateSessionInfo,
    updateAnswer
  } = useAssessment();
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [filesDialog, setFilesDialog] = useState({ open: false, questionId: null, files: [] });
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [replacingFile, setReplacingFile] = useState(null);

  const steps = ['Organization Details', 'Instructions', 'Assessment Questions', 'Review & Submit', 'Results'];

  const sessionToken = getCurrentSessionToken();

  // Initialize and validate data on mount
  useEffect(() => {
    console.log('AssessmentReview mounted with data:', assessmentData);
    
    // If sessionToken is missing from context but exists in sessionStorage, update context
    if (!assessmentData.sessionToken) {
      const sessionToken = sessionStorage.getItem('sessionToken');
      const assessmentId = sessionStorage.getItem('assessmentId');
      
      if (sessionToken) {
        console.log('Found sessionToken in storage, updating context:', sessionToken);
        updateSessionInfo(sessionToken, assessmentId ? parseInt(assessmentId) : null);
      }
    }
    
    setLoading(false);
  }, [assessmentData, updateSessionInfo]);

  const handleEdit = (questionIndex) => {
    navigate('/assessment/questions', {
      state: { startFromQuestion: questionIndex }
    });
  };

  const toggleDetailsExpansion = (questionId) => {
    setExpandedDetails(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const handleViewFiles = async (questionId) => {
    const answer = getAnswerForQuestion(questionId);
    if (!answer?.answer_id || !sessionToken) {
      console.error('No answer_id or session token available');
      return;
    }

    setLoadingFiles(true);
    setFilesDialog({ open: true, questionId, files: [] });

    try {
      // Call the evidence API to get files
      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/answers/${answer.answer_id}/evidence`);
      
      if (response.ok) {
        const files = await response.json();
        console.log('Files for question:', questionId, files);
        setFilesDialog({ open: true, questionId, files });
      } else {
        console.error('Failed to fetch files');
        setError('Failed to fetch files. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching files:', error);
      setError('Error fetching files. Please try again.');
    } finally {
      setLoadingFiles(false);
    }
  };

 const handleReplaceFile = async (questionId, evidenceId) => {
  // Create a temporary file input
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.multiple = false;
  fileInput.accept = '*/*';
  fileInput.style.display = 'none'; // Make sure it's hidden
  
  // Add it to the body temporarily
  document.body.appendChild(fileInput);

  fileInput.onchange = async (event) => {
    const file = event.target.files[0];
    
    // Clean up the input immediately after use
    document.body.removeChild(fileInput);
    
    if (!file || !sessionToken) return;

    setReplacingFile(evidenceId);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Call the replace API
      const response = await fetch(
        `https://py.taih.ai/api/assessments/${sessionToken}/answers/${questionId}/evidence/replace?evidence_id=${evidenceId}`, 
        {
          method: 'POST',
          body: formData
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('File replaced:', result);
        
        // Update the answer with new answer_id if needed
        const currentAnswer = getAnswerForQuestion(questionId) || {};
        const updatedAnswer = {
          ...currentAnswer,
          answer_id: result.answer_id,
          question_id: questionId
        };
        updateAnswer(questionId, updatedAnswer);

        // Refresh the files list
        handleViewFiles(questionId);
      } else {
        throw new Error('Failed to replace file');
      }
    } catch (error) {
      console.error('Error replacing file:', error);
      setError('Failed to replace file. Please try again.');
    } finally {
      setReplacingFile(null);
    }
  };

  // Handle case where user cancels file selection
  fileInput.oncancel = () => {
    document.body.removeChild(fileInput);
  };

  // Trigger the file picker
  fileInput.click();
};

  const closeFilesDialog = () => {
    setFilesDialog({ open: false, questionId: null, files: [] });
  };

  // Format file size helper
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Updated answered questions count to include comment validation
  const getAnsweredQuestionsCountWithComments = () => {
    return assessmentData.questions.filter(question => {
      const answer = getAnswerForQuestion(question.id);
      return answer?.answer_option_code && answer?.free_text_comment?.trim();
    }).length;
  };

  const handleSubmit = async () => {
    const answeredCount = getAnsweredQuestionsCountWithComments();
    const totalCount = getTotalQuestionsCount();
    
    if (answeredCount < totalCount) {
      setError(`Please answer all questions and provide comments. ${answeredCount}/${totalCount} completed.`);
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const sessionToken = getCurrentSessionToken();
      
      if (!sessionToken) {
        throw new Error('No session token found');
      }

      // Prepare answers array according to API format with comments
      const answersArray = Object.values(assessmentData.answers).map(answer => ({
        question_id: parseInt(answer.question_id),
        answer_option_code: answer.answer_option_code,
        free_text_comment: answer.free_text_comment || ""
      }));

      const submitData = {
        mode: "expert",
        answers: answersArray
      };

      console.log('=== SUBMIT API REQUEST ===');
      console.log('URL:', `https://py.taih.ai/api/assessments/${sessionToken}/submitAll`);
      console.log('Payload:', submitData);
      console.log('Total answers being submitted:', answersArray.length);

      const response = await fetch(`https://py.taih.ai/api/assessments/${sessionToken}/submitAll`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Submit response:', data);
        
        // Navigate to results page
        navigate('/assessment/results', {
          state: {
            submissionData: data,
            assessmentSummary: {
              totalQuestions: totalCount,
              answeredQuestions: answeredCount,
              organizationDetails: assessmentData.organizationDetails,
              sessionToken: sessionToken
            }
          }
        });
        
      } else {
        const errorData = await response.json().catch(() => ({ 
          detail: `Submission failed with status ${response.status}` 
        }));
        
        console.error('Submit API error:', errorData);
        setError(errorData.detail || 'Failed to submit assessment. Please try again.');
      }

    } catch (err) {
      console.error('Error submitting assessment:', err);
      
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the backend is running and try again.');
      } else {
        setError(err.message || 'Failed to submit assessment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getAnswerLabel = (answerCode) => {
    const answerMap = {
      'INCOMPLETE': 'Incomplete',
      'DOCUMENTED': 'Documented', 
      'OPERATIONAL': 'Operational',
      'VERIFIED': 'Verified'
    };
    return answerMap[answerCode] || answerCode;
  };

  const getAnswerColor = (answerCode) => {
    const colorMap = {
      'INCOMPLETE': 'error',
      'DOCUMENTED': 'warning',
      'OPERATIONAL': 'info', 
      'VERIFIED': 'success'
    };
    return colorMap[answerCode] || 'default';
  };

  const getAnswerIcon = (answerCode) => {
    const iconMap = {
      'INCOMPLETE': '❌',
      'DOCUMENTED': '📝',
      'OPERATIONAL': '⚙️', 
      'VERIFIED': '✅'
    };
    return iconMap[answerCode] || '❓';
  };

  // Show loading while context initializes
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress sx={{ color: '#004494' }} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading assessment data...</Typography>
      </Box>
    );
  }

  // Check if we have valid assessment data
  if (!hasValidAssessment()) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          No active assessment found. Please start a new assessment.
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            onClick={() => navigate('/register')} 
            variant="contained"
            sx={{
              backgroundColor: '#004494',
              '&:hover': {
                backgroundColor: '#003366'
              }
            }}
          >
            Start New Assessment
          </Button>
          <Button 
            onClick={() => navigate('/')} 
            variant="outlined"
            sx={{
              borderColor: '#004494',
              color: '#004494',
              '&:hover': {
                borderColor: '#003366',
                color: '#003366',
                backgroundColor: 'rgba(0, 68, 148, 0.04)'
              }
            }}
          >
            Go to Home
          </Button>
        </Box>
      </Container>
    );
  }

  // Check if we have questions
  if (!assessmentData.questions || assessmentData.questions.length === 0) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          No questions found for this assessment. Please go to questions page to load them.
        </Alert>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            onClick={() => navigate('/assessment/questions')} 
            variant="contained"
            sx={{
              backgroundColor: '#004494',
              '&:hover': {
                backgroundColor: '#003366'
              }
            }}
          >
            Go to Questions
          </Button>
          <Button 
            onClick={() => navigate('/assessment/instant')} 
            variant="outlined"
            sx={{
              borderColor: '#004494',
              color: '#004494',
              '&:hover': {
                borderColor: '#003366',
                color: '#003366',
                backgroundColor: 'rgba(0, 68, 148, 0.04)'
              }
            }}
          >
            Start New Assessment
          </Button>
        </Box>
      </Container>
    );
  }

  const answeredCount = getAnsweredQuestionsCountWithComments();
  const totalCount = getTotalQuestionsCount();
  const completionPercentage = totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;

  // Group questions by principle for better organization
  const questionsByPrinciple = assessmentData.questions.reduce((acc, question) => {
    if (!acc[question.principle]) {
      acc[question.principle] = [];
    }
    acc[question.principle].push(question);
    return acc;
  }, {});

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box sx={{ bgcolor: '#004494', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            position: 'relative'
          }}>
            <IconButton 
              onClick={() => navigate('/assessment/questions')}
              sx={{ 
                color: 'white', 
                position: 'absolute',
                left: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'white' }}>
              Review & Submit Assessment
            </Typography>
          </Box>

          {/* Progress Stepper */}
          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={3} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: 'white !important' },
                      '& .MuiStepIcon-root': {
                        color: idx <= 3 ? '#fff !important' : undefined
                      },
                      '& .MuiStepIcon-text': {
                        fill: idx <= 3 ? '#004494' : undefined
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Container>
      </Box>

      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 1200, mx: 'auto' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
            Review Your Assessment
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center' }}>
            Review all your responses, comments, and uploaded files below. Click the edit icon to modify any answer.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Progress Summary */}
          <Card sx={{ mb: 4, border: `2px solid ${completionPercentage === 100 ? '#4caf50' : '#ff9800'}` }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                    {completionPercentage === 100 ? (
                      <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                    ) : (
                      <WarningIcon color="warning" sx={{ mr: 1 }} />
                    )}
                    Assessment Progress
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {answeredCount} of {totalCount} questions completed (with answers and comments)
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ 
                    color: completionPercentage === 100 ? 'success.main' : 'warning.main',
                    fontWeight: 700
                  }}>
                    {completionPercentage}%
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Complete
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Questions and Answers Review */}
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Assessment Responses ({answeredCount}/{totalCount})
          </Typography>

          {Object.entries(questionsByPrinciple).map(([principle, questions]) => (
            <Card key={principle} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#004494' }}>
                  {principle}
                </Typography>
                
                {questions
                  .sort((a, b) => a.display_order - b.display_order)
                  .map((question) => {
                    const globalIndex = assessmentData.questions.findIndex(q => q.id === question.id);
                    const answer = getAnswerForQuestion(question.id);
                    const isAnswered = answer && answer.answer_option_code && answer.free_text_comment?.trim();
                    const hasComment = answer?.free_text_comment?.trim();
                    const hasFiles = answer?.has_files;
                    const hasDetails = hasComment || hasFiles;
                    
                    return (
                      <Paper
                        key={question.id}
                        variant="outlined"
                        sx={{ 
                          mb: 2,
                          border: '1px solid',
                          borderColor: isAnswered ? 'success.light' : 'warning.light',
                          bgcolor: isAnswered ? 'success.50' : 'warning.50',
                          overflow: 'hidden'
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  flex: 1,
                                  lineHeight: 1.4,
                                  fontWeight: 500
                                }}
                              >
                                {question.question_text}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                              {answer?.answer_option_code ? (
                                <Chip 
                                  label={getAnswerLabel(answer.answer_option_code)} 
                                  color={getAnswerColor(answer.answer_option_code)}
                                  size="small"
                                />
                              ) : (
                                <Chip 
                                  label="Not Answered" 
                                  color="warning" 
                                  size="small"
                                />
                              )}
                              
                              {/* Show details button */}
                              {hasDetails && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleDetailsExpansion(question.id)}
                                  sx={{ color: '#004494' }}
                                  title="View details"
                                >
                                  {expandedDetails[question.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              )}

                              {/* View Files button */}
                              {hasFiles && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => handleViewFiles(question.id)}
                                  sx={{ 
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    borderColor: '#004494',
                                    color: '#004494',
                                    '&:hover': {
                                      borderColor: '#003366',
                                      color: '#003366',
                                      backgroundColor: 'rgba(0, 68, 148, 0.04)'
                                    }
                                  }}
                                >
                                  View Files
                                </Button>
                              )}
                              
                              <IconButton
                                onClick={() => handleEdit(globalIndex)}
                                size="small"
                                sx={{ 
                                  color: '#004494',
                                  '&:hover': {
                                    bgcolor: 'rgba(0, 68, 148, 0.08)'
                                  }
                                }}
                                title="Edit this answer"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Details Section (Comments) */}
                          {hasDetails && (
                            <Collapse in={expandedDetails[question.id]} timeout="auto">
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ pl: 2, pr: 6 }}>
                                
                                {/* Comment Section */}
                                {hasComment && (
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1, display: 'flex', alignItems: 'center' }}>
                                      <CommentIcon sx={{ fontSize: 16, mr: 1, color: '#004494' }} />
                                      Comment:
                                    </Typography>
                                    <Paper 
                                      variant="outlined" 
                                      sx={{ 
                                        p: 2, 
                                        bgcolor: 'grey.50',
                                        borderColor: 'grey.300'
                                      }}
                                    >
                                      <Typography 
                                        variant="body2" 
                                        sx={{ 
                                          color: 'text.primary',
                                          lineHeight: 1.6,
                                          whiteSpace: 'pre-wrap'
                                        }}
                                      >
                                        {answer.free_text_comment}
                                      </Typography>
                                    </Paper>
                                  </Box>
                                )}

                                {/* Files indicator */}
                                {hasFiles && (
                                  <Box sx={{ mt: hasComment ? 2 : 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', display: 'flex', alignItems: 'center' }}>
                                      <AttachFileIcon sx={{ fontSize: 16, mr: 1, color: '#004494' }} />
                                      Supporting documents uploaded
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Collapse>
                          )}

                          {/* Show if comment is missing */}
                          {answer?.answer_option_code && !hasComment && (
                            <Box sx={{ mt: 2, pl: 2 }}>
                              <Alert severity="warning" size="small">
                                Comment required for this question
                              </Alert>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    );
                  })}
              </CardContent>
            </Card>
          ))}

          {/* Summary Statistics */}
          <Card sx={{ mb: 4, bgcolor: 'background.accent' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{ mb: 3, fontWeight: 600, textAlign: 'center' }}
              >
                Assessment Summary
              </Typography>

              <Grid
                container
                spacing={3}
                justifyContent="center"
                alignItems="center"
                textAlign="center"
              >
                {['VERIFIED', 'OPERATIONAL', 'DOCUMENTED', 'INCOMPLETE'].map(status => {
                  const count = Object.values(assessmentData.answers).filter(
                    answer => answer.answer_option_code === status
                  ).length;

                  return (
                    <Grid item xs={6} sm={3} key={status}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography
                          variant="h4"
                          sx={{
                            color:
                              getAnswerColor(status) === 'default'
                                ? 'text.primary'
                                : `${getAnswerColor(status)}.main`,
                            fontWeight: 700
                          }}
                        >
                          {count}
                        </Typography>

                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {getAnswerIcon(status)} {getAnswerLabel(status)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Additional stats for comments and files */}
              <Divider sx={{ my: 3 }} />
              <Grid container spacing={2} justifyContent="center">
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#004494', fontWeight: 600 }}>
                      {Object.values(assessmentData.answers).filter(
                        answer => answer.free_text_comment?.trim()
                      ).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      💬 With Comments
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ color: '#004494', fontWeight: 600 }}>
                      {Object.values(assessmentData.answers).filter(
                        answer => answer.has_files
                      ).length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      📎 With Files
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Submit Section */}
          <Paper 
            sx={{ 
              p: 4, 
              bgcolor: answeredCount === totalCount ? 'success.50' : 'warning.50',
              border: '2px solid',
              borderColor: answeredCount === totalCount ? 'success.main' : 'warning.main'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
              {answeredCount === totalCount 
                ? '🎉 Ready to Submit!' 
                : '⚠️ Please Complete All Questions'
              }
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
              {answeredCount === totalCount 
                ? 'All questions have been answered with comments. You can now submit your assessment for evaluation.'
                : `You have completed ${answeredCount} of ${totalCount} questions. Please provide answers and comments for all remaining questions before submitting.`
              }
            </Typography>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                onClick={() => navigate('/assessment/questions')}
                variant="outlined"
                disabled={submitting}
                size="large"
                sx={{
                  borderColor: '#004494',
                  color: '#004494',
                  '&:hover': {
                    borderColor: '#003366',
                    color: '#003366',
                    backgroundColor: 'rgba(0, 68, 148, 0.04)'
                  }
                }}
              >
                {answeredCount === totalCount ? 'Review Questions' : 'Complete Remaining Questions'}
              </Button>

              <Button
                onClick={handleSubmit}
                variant="contained"
                size="large"
                disabled={submitting || answeredCount !== totalCount}
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                sx={{ 
                  minWidth: 200,
                  backgroundColor: '#004494',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#003366'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
              >
                {submitting ? 'Submitting Assessment...' : 'Submit Assessment'}
              </Button>
            </Box>
          </Paper>
        </Paper>
      </Container>

      {/* Files Dialog */}
      <Dialog 
        open={filesDialog.open} 
        onClose={closeFilesDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Supporting Documents
          </Typography>
          <IconButton onClick={closeFilesDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {loadingFiles ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress sx={{ color: '#004494' }} />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading files...</Typography>
            </Box>
          ) : filesDialog.files.length > 0 ? (
            <List>
              {filesDialog.files.map((file, index) => (
                <ListItem 
                  key={file.evidence_id} 
                  sx={{ 
                    borderBottom: index < filesDialog.files.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    py: 2
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFileIcon sx={{ color: '#004494', fontSize: 20 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {file.file_name}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {formatFileSize(file.size_bytes)} • {file.mime_type}
                      </Typography>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={replacingFile === file.evidence_id ? <CircularProgress size={16} /> : <ReplaceIcon />}
                      onClick={() => handleReplaceFile(filesDialog.questionId, file.evidence_id)}
                      disabled={replacingFile === file.evidence_id}
                      sx={{ 
                        textTransform: 'none',
                        minWidth: 'auto',
                        px: 1.5,
                        borderColor: '#004494',
                        color: '#004494',
                        '&:hover': {
                          borderColor: '#003366',
                          color: '#003366',
                          backgroundColor: 'rgba(0, 68, 148, 0.04)'
                        }
                      }}
                    >
                      {replacingFile === file.evidence_id ? 'Replacing...' : 'Replace'}
                    </Button>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                No files found for this question.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={closeFilesDialog} 
            variant="contained"
            sx={{
              backgroundColor: '#004494',
              color: 'white',
              '&:hover': {
                backgroundColor: '#003366'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}